// Frontend i18n — translates labels/commentary only.
// English examples, suggested answers, vocabulary suggestions and the interview
// question text itself always stay in English.
//
// Bundles are keyed by BCP-47 code (en, pt-BR, es-419, fr-CA) with two
// backward-compat aliases: "pt-br" and "both" (legacy session.language codes)
// map to the pt-BR bundle. makeT() normalises whichever the caller passes.

export const STRINGS = {
  en: {
    // Common
    "brand.tagline": "AI Interview Simulator",
    "common.copy": "Copy",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Download Assessment Report",
    "rp.downloadsCardSub1": "Get your full question-by-question performance report.",
    "rp.downloadsCardBtn1": "Download Report",
    "rp.pdfFormat": "PDF format",
    // Interview page (still hardcoded on some screens — full rollout in a later phase)
    "iv.progress": (n, t, a) => `Question ${n} of ${t}   ${a} answered`,
    "iv.finish": "See my report",
    "iv.finish.busy": "Building report…",
    "iv.playQ": "Play question",
    "iv.playSlow": "Replay slower",
    "iv.stopQ": "Stop",
    "iv.loading": "Loading…",
    "iv.femaleVoice": "Female voice",
    "iv.maleVoice": "Male voice",
    "iv.restartInterview": "Restart interview",
    "iv.restartConfirm": "Restart the interview? Your current progress will be lost, but your uploaded résumé, job description and other selections will be kept.",
    "iv.recordAria": "Start recording",
    "iv.stopAria": "Stop recording",
    "iv.micAccessDenied": "Please allow microphone access in your browser.",
    "iv.interviewCompleteTitle": "Interview complete!",
    "iv.interviewCompleteBody": "Congratulations! You've successfully completed your interview. You can now view your full performance report.",
    "iv.progressCurrent": (n, total) => `Question ${n} of ${total}`,
    "iv.progressAnswered": (n) => `${n} answered`,
    "iv.hiddenH": "Listening comprehension mode",
    "iv.hiddenP": "The question text is hidden. Listen carefully to the interviewer, then answer with your voice. You can replay the question above at any time.",
    "iv.reveal": "Reveal the question text →",
    "iv.tapMic": "Tap the mic to speak",
    "iv.recording": "Recording your answer",
    "iv.recorded": "Recorded — review before sending",
    "iv.rerecord": "Re-record",
    "iv.submit": "Submit answer",
    "iv.submitting": "Transcribing & scoring…",
    "iv.transcriptLabel": "Transcript",
    "iv.previous": "← Previous",
    "iv.skipNext": "Skip to next",
    "iv.lastHint": "Last question — finish above when ready.",
    "iv.closingLabel": "Before we finish",
    "iv.closingHint": "Ask the interviewer one or two questions, as you would at the end of a real interview, for example about the role or the team. If you have none, you can say so.",
    "iv.closingNone": "I have no questions",
    "iv.closingDone": "Thank you. You can now see your report.",
    "iv.yourQuestions": "Your questions",
    "iv.hiddenSidebar": "Hidden — listen when you get here",
    "iv.answered": "Answer recorded",
    "iv.answeredSub": "You can review your feedback in the final report.",
    "iv.clarity": "Clarity",
    "iv.grammar": "Grammar",
    "iv.vocabulary": "Vocabulary",
    "iv.tone": "Tone",
    "iv.yourTranscript": "Your transcript",
    "iv.questionEyebrow": "Interview question",
    "iv.standardQuestion": "Standard question",
    "iv.roleQuestion": "Role-specific question",
    "iv.resumeQuestion": "Resume-based question",
    "iv.questionN": (n) => `Question ${n}`,
    "iv.loadFail": "Could not load your interview.",
    "iv.answerSaved": "Answer saved.",
    "iv.submitFail": "Failed to submit. Please try again.",
    "iv.reportFail": "Could not build the report yet.",
    "iv.audioPlayFail": "Could not play the question audio.",
    "iv.audioUnavailable": "The question audio is unavailable right now. Please read the question below and continue.",
    "iv.stop": "Stop",
    "iv.warmupEye": "Warm-up Question",
    "iv.warmupTitle": "Let's start with a quick warm-up",
    "iv.warmupIntro": "Before we begin the interview, we'll do a brief warm-up question. Its only purpose is to help you test your microphone, feel more comfortable speaking in English, and understand how the interview works. Your response is not evaluated and does not affect your score or reports.",
    "iv.warmupBanner": "This warm-up is just for you to get ready. Nothing is sent for analysis.",
    "iv.warmupStart": "Start recording",
    "iv.warmupContinue": "Continue to the interview",
    "iv.warmupQuestionEye": "Warm-up question",
    // Report page
    "rp.download": "Download report",
    "rp.newInterview": "New interview",
    "rp.betaFeedback": "Beta feedback",
    "rp.betaFeedbackStatusError": "Beta feedback is temporarily unavailable.",
    "rp.repracticeH": "Practice These Questions Again",
    "rp.repracticeP": "Apply your feedback by repeating the same interview questions.",
    "rp.repracticeMeta": "1 re-practice available · Uses 1 interview session",
    "rp.repracticeCta": "Practice these questions again",
    "rp.repracticeUsed": "You\u2019ve completed your re-practice attempt for this interview.",
    "rp.repracticeConfirmTitle": "Practice These Questions Again?",
    "rp.repracticeConfirmIntro": "You can re-practice this interview once using the exact same questions. This will use 1 interview session.",
    "rp.repracticeConfirmBeforeH": "Before you start:",
    "rp.repracticeConfirmBullet1": "Your Performance Report will remain available to download until your 30-day access ends.",
    "rp.repracticeConfirmBullet2": "Do not log out. Your Re-Practice must be completed during your current login session.",
    "rp.repracticeConfirmYes": "Start Re-Practice",
    "rp.repracticeConfirmNo": "Cancel",
    "rp.repracticeStarting": "Starting re-practice\u2026",
    "rp.repracticeErrGeneric": "Could not start the re-practice. Please try again.",
    "rp.repracticeErrAlreadyUsed": "You\u2019ve already used your re-practice for this interview.",
    "rp.repracticeErrNoCredits": "You’ve used all the interview sessions in your 30-day access. Your reports are still available.",
    "rp.overall": "Overall performance",
    "rp.composite": "Composite of clarity · grammar · vocabulary · professional tone",
    "rp.answeredOf": (a, t) => `You completed ${a} of ${t} questions. Scroll down for per-question feedback — clarity, grammar, vocabulary and professional tone.`,
    "rp.clarity": "Clarity",
    "rp.grammar": "Grammar",
    "rp.vocabulary": "Vocabulary",
    "rp.protone": "Professional tone",
    "rp.fluency": "Fluency",
    "rp.relevance": "Relevance to the question",
    "rp.supportingEvidence": "Supporting evidence",
    "rp.organization": "Organization & structure",
    "rp.professionalCompetency": "Professional competency",
    "rp.evidenceOfResults": "Evidence of results",
    "rp.professionalPresence": "Professional presence",
    "rp.interviewPerformance": "Interview performance",
    "rp.englishLanguage": "Professional English communication",
    "rp.englishInsufficient": "Answer too short to assess English.",
    "rp.englishOtherLanguage": "Not answered in English, so English wasn't assessed.",
    "rp.englishAssessedOf": (a, t) => `assessed on ${a} of ${t} answers`,
    "rp.summary": "Summary",
    "rp.summaryH": "Your Interview Performance at a Glance",
    "rp.summaryP": "Below is your question-by-question feedback. Review what you did well, where you can improve, and the areas that will have the greatest impact on your next interview.",
    "rp.bestArea": "Best area",
    "rp.focusNext": "Focus next",
    "rp.perQuestion": "Per-question feedback",
    "rp.closingH": "Your questions for the interviewer",
    "rp.closingIntro": "At the end, the interviewer asked: “Do you have any questions for me?” This part isn’t in your Interview Performance score; your English here counts toward your English score.",
    "rp.closingSaid": "What you said",
    "rp.closingNone": "You didn’t ask any questions.",
    "rp.closingTips": "Tips",
    "rp.closingSuggested": "Questions you could ask next time",
    "rp.answeredCount": (a, t) => `${a} of ${t} answered`,
    "rp.notAnswered": "Not answered",
    "rp.overallShort": "Overall",
    "rp.yourTranscript": "Your transcript",
    "rp.noAnswerGiven": "No answer given (the time to start answering ran out).",
    "rp.profanityNote": "Your answer appears to have included one or more statements of profanity. In general, profanity is not acceptable during a job interview and should be avoided.",
    "rp.suggestedAnswer": "Suggested polished answer",
    "rp.strengths": "Strengths",
    "rp.improve": "Improve",
    // Job match
    "jm.title": "Job match",
    "jm.subtitle": "How your resume + answers stack up against the required skills in the job description.",
    "jm.matchScore": "Match score",
    "jm.matched": "matched",
    "jm.partial": "partial",
    "jm.gap": "gap",
    "jm.gaps": "gaps",
    "jm.covered": (m, t) => `Required skills · ${m}/${t} covered`,
    "jm.statusMatch": "Match",
    "jm.statusPartial": "Partial",
    "jm.statusGap": "Gap",
    "sp.hGaps": (n) => "top " + (n === 1 ? "priority" : `${n} gaps`),
    // Pre-interview match preview (upload screen)
    // Seniority (AI-detected, read-only)
    "sen.eyebrow": "Interview level (detected)",
    "sen.entry":      "Entry level",
    "sen.mid":        "Mid level",
    "sen.senior":     "Senior IC",
    "sen.managerial": "Managerial",
    "sen.executive":  "Executive",
    "sen.hintDocs": "We calibrated the interview to this level based on your documents. No manual override — that's the point of a realistic simulation.",
  },
  "pt-BR": {
    "brand.tagline": "Simulador de Entrevistas com IA",
    "common.copy": "Copiar",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Baixar Relatório de Avaliação",
    "rp.downloadsCardSub1": "Baixe seu relatório completo de desempenho, pergunta por pergunta.",
    "rp.downloadsCardBtn1": "Baixar Relatório",
    "rp.pdfFormat": "Formato PDF",
    "iv.progress": (n, t, a) => `Pergunta ${n} de ${t}   ${a} respondida${a === 1 ? "" : "s"}`,
    "iv.finish": "Ver meu relatório",
    "iv.finish.busy": "Montando o relatório…",
    "iv.playQ": "Ouvir pergunta",
    "iv.playSlow": "Ouvir mais devagar",
    "iv.stopQ": "Parar",
    "iv.loading": "Carregando…",
    "iv.femaleVoice": "Voz feminina",
    "iv.maleVoice": "Voz masculina",
    "iv.restartInterview": "Reiniciar entrevista",
    "iv.restartConfirm": "Reiniciar a entrevista? Seu progresso atual será perdido, mas o currículo, a descrição da vaga e as demais escolhas serão mantidos.",
    "iv.recordAria": "Iniciar gravação",
    "iv.stopAria": "Parar gravação",
    "iv.micAccessDenied": "Permita o acesso ao microfone no seu navegador.",
    "iv.interviewCompleteTitle": "Entrevista concluída!",
    "iv.interviewCompleteBody": "Parabéns! Você concluiu sua entrevista com sucesso. Agora você pode visualizar seu relatório completo de desempenho.",
    "iv.progressCurrent": (n, total) => `Pergunta ${n} de ${total}`,
    "iv.progressAnswered": (n) => `${n} respondida${n === 1 ? "" : "s"}`,
    "iv.hiddenH": "Modo de compreensão auditiva",
    "iv.hiddenP": "O texto da pergunta está oculto. Ouça o entrevistador com atenção e responda com a sua voz. Você pode reproduzir a pergunta acima quando quiser.",
    "iv.reveal": "Mostrar a pergunta →",
    "iv.tapMic": "Toque no microfone para falar",
    "iv.recording": "Gravando a sua resposta",
    "iv.recorded": "Gravada — revise antes de enviar",
    "iv.rerecord": "Regravar",
    "iv.submit": "Enviar resposta",
    "iv.submitting": "Transcrevendo e avaliando…",
    "iv.transcriptLabel": "Transcrição",
    "iv.previous": "← Anterior",
    "iv.skipNext": "Pular para a próxima",
    "iv.lastHint": "Última pergunta — conclua acima quando estiver pronto.",
    "iv.closingLabel": "Antes de terminar",
    "iv.closingHint": "Faça uma ou duas perguntas ao entrevistador, como no final de uma entrevista real, por exemplo sobre a função ou a equipe. Se não tiver nenhuma, você pode dizer isso.",
    "iv.closingNone": "Não tenho perguntas",
    "iv.closingDone": "Obrigado. Agora você pode ver seu relatório.",
    "iv.yourQuestions": "Suas perguntas",
    "iv.hiddenSidebar": "Oculta — escute quando chegar aqui",
    "iv.answered": "Resposta gravada",
    "iv.answeredSub": "Você pode revisar o seu feedback no relatório final.",
    "iv.clarity": "Clareza",
    "iv.grammar": "Gramática",
    "iv.vocabulary": "Vocabulário",
    "iv.tone": "Tom",
    "iv.yourTranscript": "Sua transcrição",
    "iv.questionEyebrow": "Pergunta da entrevista",
    "iv.standardQuestion": "Pergunta clássica",
    "iv.roleQuestion": "Pergunta sobre a vaga",
    "iv.resumeQuestion": "Pergunta baseada no currículo",
    "iv.questionN": (n) => `Pergunta ${n}`,
    "iv.loadFail": "Não foi possível carregar sua entrevista.",
    "iv.answerSaved": "Resposta salva.",
    "iv.submitFail": "Falha ao enviar. Tente novamente.",
    "iv.reportFail": "Ainda não foi possível montar o relatório.",
    "iv.audioPlayFail": "Não foi possível reproduzir o áudio da pergunta.",
    "iv.audioUnavailable": "O áudio da pergunta está indisponível no momento. Leia a pergunta abaixo e continue.",
    "iv.stop": "Parar",
    "iv.warmupEye": "Pergunta de Aquecimento",
    "iv.warmupTitle": "Vamos começar com um aquecimento",
    "iv.warmupIntro": "Antes de iniciarmos a entrevista, faremos uma breve pergunta de aquecimento. Ela tem apenas o objetivo de ajudá-lo(a) a testar o microfone, sentir-se mais confortável falando em inglês e conhecer o funcionamento da entrevista. Sua resposta não será avaliada e não fará parte da sua pontuação ou dos relatórios da entrevista.",
    "iv.warmupBanner": "Este aquecimento é apenas para você se preparar. Nada será enviado para análise.",
    "iv.warmupStart": "Começar a gravar",
    "iv.warmupContinue": "Continuar para a entrevista",
    "iv.warmupQuestionEye": "Pergunta de aquecimento",
    "rp.download": "Baixar relatório",
    "rp.newInterview": "Nova entrevista",
    "rp.betaFeedback": "Feedback beta",
    "rp.betaFeedbackStatusError": "O feedback beta está temporariamente indisponível.",
    "rp.repracticeH": "Pratique Estas Perguntas Novamente",
    "rp.repracticeP": "Aplique seu feedback repetindo as mesmas perguntas da entrevista.",
    "rp.repracticeMeta": "1 nova prática disponível · Usa 1 sessão de entrevista",
    "rp.repracticeCta": "Praticar estas perguntas novamente",
    "rp.repracticeUsed": "Você já concluiu sua tentativa de nova prática para esta entrevista.",
    "rp.repracticeConfirmTitle": "Praticar Estas Perguntas Novamente?",
    "rp.repracticeConfirmIntro": "Você pode praticar esta entrevista novamente uma vez usando exatamente as mesmas perguntas. Isto usará 1 sessão de entrevista.",
    "rp.repracticeConfirmBeforeH": "Antes de começar:",
    "rp.repracticeConfirmBullet1": "Seu Relatório de Desempenho continuará disponível para download até o fim do seu acesso de 30 dias.",
    "rp.repracticeConfirmBullet2": "Não saia da sua conta. Sua Nova Prática deve ser concluída durante sua sessão de login atual.",
    "rp.repracticeConfirmYes": "Iniciar Nova Prática",
    "rp.repracticeConfirmNo": "Cancelar",
    "rp.repracticeStarting": "Iniciando a nova prática\u2026",
    "rp.repracticeErrGeneric": "Não foi possível iniciar a nova prática. Tente novamente.",
    "rp.repracticeErrAlreadyUsed": "Você já usou sua nova prática para esta entrevista.",
    "rp.repracticeErrNoCredits": "Você já usou todas as sessões de entrevista do seu acesso de 30 dias. Seus relatórios continuam disponíveis.",
    "rp.overall": "Desempenho geral",
    "rp.composite": "Média de clareza · gramática · vocabulário · tom profissional",
    "rp.answeredOf": (a, t) => `Você respondeu ${a} de ${t} perguntas. Role para baixo para ver o feedback por pergunta — clareza, gramática, vocabulário e tom profissional.`,
    "rp.clarity": "Clareza",
    "rp.grammar": "Gramática",
    "rp.vocabulary": "Vocabulário",
    "rp.protone": "Tom profissional",
    "rp.fluency": "Fluência",
    "rp.relevance": "Relevância à pergunta",
    "rp.supportingEvidence": "Evidências e exemplos",
    "rp.organization": "Organização e estrutura",
    "rp.professionalCompetency": "Competência profissional",
    "rp.evidenceOfResults": "Evidência de resultados",
    "rp.professionalPresence": "Presença profissional",
    "rp.interviewPerformance": "Desempenho na entrevista",
    "rp.englishLanguage": "Comunicação em Inglês Profissional",
    "rp.englishInsufficient": "Resposta muito curta para avaliar o inglês.",
    "rp.englishOtherLanguage": "Não respondida em inglês, por isso o inglês não foi avaliado.",
    "rp.englishAssessedOf": (a, t) => `avaliado em ${a} de ${t} respostas`,
    "rp.summary": "Resumo",
    "rp.summaryH": "Seu Desempenho na Entrevista em Resumo",
    "rp.summaryP": "Abaixo está seu feedback, pergunta por pergunta. Veja o que você fez bem, onde pode melhorar e quais áreas terão o maior impacto na sua próxima entrevista.",
    "rp.bestArea": "Ponto mais forte",
    "rp.focusNext": "Foco a seguir",
    "rp.perQuestion": "Feedback por pergunta",
    "rp.closingH": "Suas perguntas para o entrevistador",
    "rp.closingIntro": "No final, o entrevistador perguntou: “Do you have any questions for me?” Esta parte não entra na sua pontuação de Desempenho na Entrevista; seu inglês aqui conta para sua pontuação de inglês.",
    "rp.closingSaid": "O que você disse",
    "rp.closingNone": "Você não fez nenhuma pergunta.",
    "rp.closingTips": "Dicas",
    "rp.closingSuggested": "Perguntas que você poderia fazer na próxima vez",
    "rp.answeredCount": (a, t) => `${a} de ${t} respondida${t === 1 ? "" : "s"}`,
    "rp.notAnswered": "Sem resposta",
    "rp.overallShort": "Geral",
    "rp.yourTranscript": "Sua transcrição",
    "rp.noAnswerGiven": "Nenhuma resposta (o tempo para começar a responder acabou).",
    "rp.profanityNote": "Sua resposta parece ter incluído um ou mais palavrões. Em geral, palavrões não são aceitáveis em uma entrevista de emprego e devem ser evitados.",
    "rp.suggestedAnswer": "Resposta modelo sugerida (em inglês)",
    "rp.strengths": "Pontos fortes",
    "rp.improve": "A melhorar",
    "jm.title": "Compatibilidade com a vaga",
    "jm.subtitle": "Como o seu currículo + respostas se comparam com as habilidades exigidas na descrição da vaga.",
    "jm.matchScore": "Pontuação de compatibilidade",
    "jm.matched": "compatível",
    "jm.partial": "parcial",
    "jm.gap": "lacuna",
    "jm.gaps": "lacunas",
    "jm.covered": (m, t) => `Habilidades exigidas · ${m}/${t} contempladas`,
    "jm.statusMatch": "Compatível",
    "jm.statusPartial": "Parcial",
    "jm.statusGap": "Lacuna",
    "sp.hGaps": (n) => "suas " + (n === 1 ? "principais lacunas" : `${n} principais lacunas`),
    "sen.eyebrow": "Nível da entrevista (detectado)",
    "sen.entry":      "Nível inicial",
    "sen.mid":        "Nível pleno",
    "sen.senior":     "Sênior",
    "sen.managerial": "Gestão",
    "sen.executive":  "Executivo(a)",
    "sen.hintDocs": "Ajustamos a entrevista para este nível com base nos seus documentos. Não é possível alterar — é justamente isso que torna a simulação realista.",
  },

  // Latin American Spanish — used on the Report page (Component 2). Other
  // pages fall back to English keys until their component turn.
  "es-419": {
    "brand.tagline": "Simulador de Entrevistas con IA",
    "common.copy": "Copiar",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Descargar Informe de Evaluación",
    "rp.downloadsCardSub1": "Descargue su informe completo de desempeño, pregunta por pregunta.",
    "rp.downloadsCardBtn1": "Descargar Informe",
    "rp.pdfFormat": "Formato PDF",
    // Interview page
    "iv.progress": (n, t, a) => `Pregunta ${n} de ${t}   ${a} respondida${a === 1 ? "" : "s"}`,
    "iv.finish": "Ver mi informe",
    "iv.finish.busy": "Generando el informe…",
    "iv.playQ": "Escuchar pregunta",
    "iv.playSlow": "Escuchar más despacio",
    "iv.stopQ": "Detener",
    "iv.stop": "Detener",
    "iv.warmupEye": "Pregunta de Calentamiento",
    "iv.warmupTitle": "Comencemos con un breve calentamiento",
    "iv.warmupIntro": "Antes de iniciar la entrevista, haremos una breve pregunta de calentamiento. Su único propósito es ayudarle a probar el micrófono, sentirse más cómodo/a hablando en inglés y familiarizarse con el funcionamiento de la entrevista. Su respuesta no será evaluada ni afectará su puntuación ni los informes.",
    "iv.warmupBanner": "Este calentamiento es solo para que se prepare. Nada se envía para análisis.",
    "iv.warmupStart": "Comenzar a grabar",
    "iv.warmupContinue": "Continuar a la entrevista",
    "iv.warmupQuestionEye": "Pregunta de calentamiento",
    "iv.loading": "Cargando…",
    "iv.femaleVoice": "Voz femenina",
    "iv.maleVoice": "Voz masculina",
    "iv.restartInterview": "Reiniciar entrevista",
    "iv.restartConfirm": "¿Reiniciar la entrevista? Su progreso actual se perderá, pero conservaremos su currículum, la descripción del puesto y sus otras selecciones.",
    "iv.recordAria": "Iniciar grabación",
    "iv.stopAria": "Detener grabación",
    "iv.micAccessDenied": "Permita el acceso al micrófono en su navegador.",
    "iv.interviewCompleteTitle": "¡Entrevista completada!",
    "iv.interviewCompleteBody": "¡Felicidades! Completó su entrevista con éxito. Ahora puede ver su informe completo de desempeño.",
    "iv.progressCurrent": (n, total) => `Pregunta ${n} de ${total}`,
    "iv.progressAnswered": (n) => `${n} respondida${n === 1 ? "" : "s"}`,
    "iv.hiddenH": "Modo de comprensión auditiva",
    "iv.hiddenP": "El texto de la pregunta está oculto. Escuche atentamente al entrevistador y responda con su voz. Puede reproducir la pregunta arriba cuando quiera.",
    "iv.reveal": "Mostrar la pregunta →",
    "iv.tapMic": "Toque el micrófono para hablar",
    "iv.recording": "Grabando su respuesta",
    "iv.recorded": "Grabada — revise antes de enviar",
    "iv.rerecord": "Volver a grabar",
    "iv.submit": "Enviar respuesta",
    "iv.submitting": "Transcribiendo y evaluando…",
    "iv.transcriptLabel": "Transcripción",
    "iv.previous": "← Anterior",
    "iv.skipNext": "Saltar a la siguiente",
    "iv.lastHint": "Última pregunta — finalice arriba cuando esté listo/a.",
    "iv.closingLabel": "Antes de terminar",
    "iv.closingHint": "Hágale una o dos preguntas al entrevistador, como al final de una entrevista real, por ejemplo sobre el puesto o el equipo. Si no tiene ninguna, puede decirlo.",
    "iv.closingNone": "No tengo preguntas",
    "iv.closingDone": "Gracias. Ya puede ver su informe.",
    "iv.yourQuestions": "Sus preguntas",
    "iv.hiddenSidebar": "Oculta — escuche cuando llegue aquí",
    "iv.answered": "Respuesta grabada",
    "iv.answeredSub": "Puede revisar su feedback en el informe final.",
    "iv.clarity": "Claridad",
    "iv.grammar": "Gramática",
    "iv.vocabulary": "Vocabulario",
    "iv.tone": "Tono",
    "iv.yourTranscript": "Su transcripción",
    "iv.questionEyebrow": "Pregunta de la entrevista",
    "iv.standardQuestion": "Pregunta clásica",
    "iv.roleQuestion": "Pregunta sobre el puesto",
    "iv.resumeQuestion": "Pregunta basada en el currículum",
    "iv.questionN": (n) => `Pregunta ${n}`,
    "iv.loadFail": "No pudimos cargar su entrevista.",
    "iv.answerSaved": "Respuesta guardada.",
    "iv.submitFail": "Error al enviar. Inténtelo de nuevo.",
    "iv.reportFail": "Aún no pudimos generar el informe.",
    "iv.audioPlayFail": "No pudimos reproducir el audio de la pregunta.",
    "iv.audioUnavailable": "El audio de la pregunta no está disponible en este momento. Lea la pregunta a continuación y continúe.",
    "rp.download": "Descargar informe",
    "rp.newInterview": "Nueva entrevista",
    "rp.betaFeedback": "Feedback beta",
    "rp.betaFeedbackStatusError": "El feedback beta no está disponible por el momento.",
    "rp.repracticeH": "Practique Estas Preguntas Nuevamente",
    "rp.repracticeP": "Aplique sus comentarios repitiendo las mismas preguntas de la entrevista.",
    "rp.repracticeMeta": "1 nueva práctica disponible · Usa 1 sesión de entrevista",
    "rp.repracticeCta": "Practicar estas preguntas nuevamente",
    "rp.repracticeUsed": "Ya completó su intento de nueva práctica para esta entrevista.",
    "rp.repracticeConfirmTitle": "\u00bfPracticar Estas Preguntas Nuevamente?",
    "rp.repracticeConfirmIntro": "Usted puede practicar esta entrevista una vez más utilizando exactamente las mismas preguntas. Esto utilizará 1 sesión de entrevista.",
    "rp.repracticeConfirmBeforeH": "Antes de comenzar:",
    "rp.repracticeConfirmBullet1": "Su Informe de Desempeño seguirá disponible para descargar hasta que termine su acceso de 30 días.",
    "rp.repracticeConfirmBullet2": "No cierre sesión. Su Nueva Práctica debe completarse durante su sesión actual.",
    "rp.repracticeConfirmYes": "Iniciar Nueva Práctica",
    "rp.repracticeConfirmNo": "Cancelar",
    "rp.repracticeStarting": "Iniciando la nueva práctica\u2026",
    "rp.repracticeErrGeneric": "No se pudo iniciar la nueva práctica. Inténtelo de nuevo.",
    "rp.repracticeErrAlreadyUsed": "Ya utilizó su nueva práctica para esta entrevista.",
    "rp.repracticeErrNoCredits": "Ya utilizó todas las sesiones de entrevista de su acceso de 30 días. Sus informes siguen disponibles.",
    "rp.overall": "Desempeño general",
    "rp.composite": "Composición de claridad · gramática · vocabulario · tono profesional",
    "rp.answeredOf": (a, t) => `Completó ${a} de ${t} preguntas. Desplácese hacia abajo para ver el feedback pregunta por pregunta — claridad, gramática, vocabulario y tono profesional.`,
    "rp.clarity": "Claridad",
    "rp.grammar": "Gramática",
    "rp.vocabulary": "Vocabulario",
    "rp.protone": "Tono profesional",
    "rp.fluency": "Fluidez",
    "rp.relevance": "Relevancia a la pregunta",
    "rp.supportingEvidence": "Evidencia y ejemplos",
    "rp.organization": "Organización y estructura",
    "rp.professionalCompetency": "Competencia profesional",
    "rp.evidenceOfResults": "Evidencia de resultados",
    "rp.professionalPresence": "Presencia profesional",
    "rp.interviewPerformance": "Desempeño en la entrevista",
    "rp.englishLanguage": "Comunicación en Inglés Profesional",
    "rp.englishInsufficient": "Respuesta demasiado breve para evaluar su inglés.",
    "rp.englishOtherLanguage": "No se respondió en inglés, por lo que no se evaluó el inglés.",
    "rp.englishAssessedOf": (a, t) => `evaluado en ${a} de ${t} respuestas`,
    "rp.summary": "Resumen",
    "rp.summaryH": "Su Desempeño en la Entrevista de un Vistazo",
    "rp.summaryP": "A continuación encontrará comentarios sobre cada pregunta. Revise lo que hizo bien, dónde puede mejorar y qué áreas tendrán el mayor impacto en su próxima entrevista.",
    "rp.bestArea": "Punto más fuerte",
    "rp.focusNext": "Próximo enfoque",
    "rp.perQuestion": "Feedback por pregunta",
    "rp.closingH": "Sus preguntas para el entrevistador",
    "rp.closingIntro": "Al final, el entrevistador preguntó: “Do you have any questions for me?” Esta parte no cuenta en su puntuación de Desempeño en la Entrevista; su inglés aquí cuenta para su puntuación de inglés.",
    "rp.closingSaid": "Lo que dijo",
    "rp.closingNone": "No hizo ninguna pregunta.",
    "rp.closingTips": "Consejos",
    "rp.closingSuggested": "Preguntas que podría hacer la próxima vez",
    "rp.answeredCount": (a, t) => `${a} de ${t} respondida${t === 1 ? "" : "s"}`,
    "rp.notAnswered": "Sin respuesta",
    "rp.overallShort": "General",
    "rp.yourTranscript": "Su transcripción",
    "rp.noAnswerGiven": "Sin respuesta (se acabó el tiempo para empezar a responder).",
    "rp.profanityNote": "Su respuesta parece haber incluido una o más groserías. En general, las groserías no son aceptables en una entrevista de trabajo y deben evitarse.",
    "rp.suggestedAnswer": "Respuesta modelo sugerida (en inglés)",
    "rp.strengths": "Fortalezas",
    "rp.improve": "A mejorar",
    "jm.title": "Compatibilidad con el puesto",
    "jm.subtitle": "Cómo su currículum + respuestas se comparan con las habilidades requeridas en la descripción del puesto.",
    "jm.matchScore": "Puntuación de compatibilidad",
    "jm.matched": "compatible",
    "jm.partial": "parcial",
    "jm.gap": "brecha",
    "jm.gaps": "brechas",
    "jm.covered": (m, t) => `Habilidades requeridas · ${m}/${t} cubiertas`,
    "jm.statusMatch": "Compatible",
    "jm.statusPartial": "Parcial",
    "jm.statusGap": "Brecha",
    "sp.hGaps": (n) => (n === 1 ? "su principal brecha" : `sus ${n} principales brechas`),
    "sen.eyebrow": "Nivel de la entrevista (detectado)",
    "sen.entry":      "Nivel inicial",
    "sen.mid":        "Nivel medio",
    "sen.senior":     "Sénior IC",
    "sen.managerial": "Gerencial",
    "sen.executive":  "Ejecutivo/a",
    "sen.hintDocs": "Calibramos la entrevista a este nivel según sus documentos. No hay opción manual — ese es el punto de una simulación realista.",
  },

  // Canadian French — used on the Report page (Component 2).
  "fr-CA": {
    "brand.tagline": "Simulateur d’entrevues avec IA",
    "common.copy": "Copier",
    "common.pdf": "PDF",
    "rp.downloadsCardH1": "Télécharger le rapport d’évaluation",
    "rp.downloadsCardSub1": "Téléchargez votre rapport de performance complet, question par question.",
    "rp.downloadsCardBtn1": "Télécharger le rapport",
    "rp.pdfFormat": "Format PDF",
    // Interview page
    "iv.progress": (n, t, a) => `Question ${n} sur ${t}   ${a} répondue${a === 1 ? "" : "s"}`,
    "iv.finish": "Voir mon rapport",
    "iv.finish.busy": "Préparation du rapport…",
    "iv.playQ": "Écouter la question",
    "iv.playSlow": "Réécouter plus lentement",
    "iv.stopQ": "Arrêter",
    "iv.stop": "Arrêter",
    "iv.warmupEye": "Question d’échauffement",
    "iv.warmupTitle": "Commençons par un échauffement",
    "iv.warmupIntro": "Avant de commencer l’entrevue, nous allons faire une brève question d’échauffement. Son seul but est de vous aider à tester votre microphone, à vous sentir plus à l’aise pour parler en anglais et à comprendre comment se déroule l’entrevue. Votre réponse n’est pas évaluée et n’affecte ni votre note ni les rapports.",
    "iv.warmupBanner": "Cet échauffement sert uniquement à vous préparer. Rien n’est envoyé pour analyse.",
    "iv.warmupStart": "Commencer l’enregistrement",
    "iv.warmupContinue": "Continuer vers l’entrevue",
    "iv.warmupQuestionEye": "Question d’échauffement",
    "iv.loading": "Chargement…",
    "iv.femaleVoice": "Voix féminine",
    "iv.maleVoice": "Voix masculine",
    "iv.restartInterview": "Recommencer l’entrevue",
    "iv.restartConfirm": "Recommencer l’entrevue ? Votre progression actuelle sera perdue, mais nous conserverons votre CV, la description du poste et vos autres sélections.",
    "iv.recordAria": "Démarrer l’enregistrement",
    "iv.stopAria": "Arrêter l’enregistrement",
    "iv.micAccessDenied": "Autorisez l’accès au microphone dans votre navigateur.",
    "iv.interviewCompleteTitle": "Entrevue terminée !",
    "iv.interviewCompleteBody": "Félicitations ! Vous avez terminé votre entrevue avec succès. Vous pouvez maintenant consulter votre rapport de performance complet.",
    "iv.progressCurrent": (n, total) => `Question ${n} sur ${total}`,
    "iv.progressAnswered": (n) => `${n} répondue${n === 1 ? "" : "s"}`,
    "iv.hiddenH": "Mode compréhension auditive",
    "iv.hiddenP": "Le texte de la question est masqué. Écoutez attentivement l’intervieweur et répondez à voix haute. Vous pouvez rejouer la question ci-dessus à tout moment.",
    "iv.reveal": "Afficher la question →",
    "iv.tapMic": "Touchez le micro pour parler",
    "iv.recording": "Enregistrement de votre réponse",
    "iv.recorded": "Enregistrée — révisez avant l’envoi",
    "iv.rerecord": "Réenregistrer",
    "iv.submit": "Envoyer la réponse",
    "iv.submitting": "Transcription et évaluation…",
    "iv.transcriptLabel": "Transcription",
    "iv.previous": "← Précédente",
    "iv.skipNext": "Passer à la suivante",
    "iv.lastHint": "Dernière question — terminez ci-dessus quand vous êtes prêt(e).",
    "iv.closingLabel": "Avant de terminer",
    "iv.closingHint": "Posez une ou deux questions à l’intervieweur, comme à la fin d’une vraie entrevue, par exemple sur le poste ou l’équipe. Si vous n’en avez pas, vous pouvez le dire.",
    "iv.closingNone": "Je n’ai pas de questions",
    "iv.closingDone": "Merci. Vous pouvez maintenant voir votre rapport.",
    "iv.yourQuestions": "Vos questions",
    "iv.hiddenSidebar": "Masquée — écoutez quand vous arrivez ici",
    "iv.answered": "Réponse enregistrée",
    "iv.answeredSub": "Vous pouvez revoir votre rétroaction dans le rapport final.",
    "iv.clarity": "Clarté",
    "iv.grammar": "Grammaire",
    "iv.vocabulary": "Vocabulaire",
    "iv.tone": "Ton",
    "iv.yourTranscript": "Votre transcription",
    "iv.questionEyebrow": "Question d’entrevue",
    "iv.standardQuestion": "Question classique",
    "iv.roleQuestion": "Question spécifique au poste",
    "iv.resumeQuestion": "Question basée sur le CV",
    "iv.questionN": (n) => `Question ${n}`,
    "iv.loadFail": "Impossible de charger votre entrevue.",
    "iv.answerSaved": "Réponse enregistrée.",
    "iv.submitFail": "Échec de l’envoi. Réessayez.",
    "iv.reportFail": "Impossible de générer le rapport pour l’instant.",
    "iv.audioPlayFail": "Impossible de lire l’audio de la question.",
    "iv.audioUnavailable": "L’audio de la question n’est pas disponible pour le moment. Veuillez lire la question ci-dessous et continuer.",
    "rp.download": "Télécharger le rapport",
    "rp.newInterview": "Nouvelle entrevue",
    "rp.betaFeedback": "Feedback bêta",
    "rp.betaFeedbackStatusError": "Le feedback bêta est temporairement indisponible.",
    "rp.repracticeH": "Pratiquez de nouveau ces questions",
    "rp.repracticeP": "Appliquez votre rétroaction en refaisant les mêmes questions d\u2019entrevue.",
    "rp.repracticeMeta": "1 nouvelle pratique disponible · Utilise 1 session d\u2019entrevue",
    "rp.repracticeCta": "Pratiquer de nouveau ces questions",
    "rp.repracticeUsed": "Vous avez terminé votre tentative de nouvelle pratique pour cette entrevue.",
    "rp.repracticeConfirmTitle": "Pratiquer de nouveau ces questions?",
    "rp.repracticeConfirmIntro": "Vous pouvez refaire cette entrevue une seule fois avec exactement les mêmes questions. Cela utilisera 1 session d\u2019entrevue.",
    "rp.repracticeConfirmBeforeH": "Avant de commencer :",
    "rp.repracticeConfirmBullet1": "Votre rapport de rendement restera disponible au téléchargement jusqu’à la fin de votre accès de 30 jours.",
    "rp.repracticeConfirmBullet2": "Ne vous déconnectez pas. Votre Nouvelle pratique doit être terminée pendant votre session de connexion actuelle.",
    "rp.repracticeConfirmYes": "Démarrer la Nouvelle pratique",
    "rp.repracticeConfirmNo": "Annuler",
    "rp.repracticeStarting": "Démarrage de la nouvelle pratique\u2026",
    "rp.repracticeErrGeneric": "Impossible de démarrer la nouvelle pratique. Veuillez réessayer.",
    "rp.repracticeErrAlreadyUsed": "Vous avez déjà utilisé votre nouvelle pratique pour cette entrevue.",
    "rp.repracticeErrNoCredits": "Vous avez utilisé toutes les sessions d’entrevue de votre accès de 30 jours. Vos rapports restent disponibles.",
    "rp.overall": "Performance globale",
    "rp.composite": "Composite de clarté · grammaire · vocabulaire · ton professionnel",
    "rp.answeredOf": (a, t) => `Vous avez complété ${a} des ${t} questions. Faites défiler vers le bas pour voir la rétroaction question par question — clarté, grammaire, vocabulaire et ton professionnel.`,
    "rp.clarity": "Clarté",
    "rp.grammar": "Grammaire",
    "rp.vocabulary": "Vocabulaire",
    "rp.protone": "Ton professionnel",
    "rp.fluency": "Fluidité",
    "rp.relevance": "Pertinence à la question",
    "rp.supportingEvidence": "Preuves et exemples",
    "rp.organization": "Organisation et structure",
    "rp.professionalCompetency": "Compétence professionnelle",
    "rp.evidenceOfResults": "Preuve de résultats",
    "rp.professionalPresence": "Présence professionnelle",
    "rp.interviewPerformance": "Performance en entrevue",
    "rp.englishLanguage": "Communication en anglais professionnel",
    "rp.englishInsufficient": "Réponse trop courte pour évaluer votre anglais.",
    "rp.englishOtherLanguage": "Réponse non donnée en anglais, l’anglais n’a donc pas été évalué.",
    "rp.englishAssessedOf": (a, t) => `évalué sur ${a} des ${t} réponses`,
    "rp.summary": "Résumé",
    "rp.summaryH": "Votre Performance en Entrevue en un Coup d\u2019Œil",
    "rp.summaryP": "Vous trouverez ci-dessous votre rétroaction, question par question. Voyez ce que vous avez bien réussi, les aspects que vous pouvez améliorer et les éléments qui auront le plus grand impact sur votre prochaine entrevue.",
    "rp.bestArea": "Point le plus fort",
    "rp.focusNext": "Prochain focus",
    "rp.perQuestion": "Rétroaction par question",
    "rp.closingH": "Vos questions pour l’intervieweur",
    "rp.closingIntro": "À la fin, l’intervieweur a demandé : « Do you have any questions for me? » Cette partie ne compte pas dans votre résultat en Performance en entrevue; votre anglais ici compte dans votre résultat en anglais.",
    "rp.closingSaid": "Ce que vous avez dit",
    "rp.closingNone": "Vous n’avez posé aucune question.",
    "rp.closingTips": "Conseils",
    "rp.closingSuggested": "Questions que vous pourriez poser la prochaine fois",
    "rp.answeredCount": (a, t) => `${a} sur ${t} répondue${t === 1 ? "" : "s"}`,
    "rp.notAnswered": "Sans réponse",
    "rp.overallShort": "Global",
    "rp.yourTranscript": "Votre transcription",
    "rp.noAnswerGiven": "Aucune réponse (le temps pour commencer à répondre est écoulé).",
    "rp.profanityNote": "Votre réponse semble contenir un ou plusieurs jurons. En général, les jurons ne sont pas acceptables lors d’une entrevue d’emploi et doivent être évités.",
    "rp.suggestedAnswer": "Réponse modèle suggérée (en anglais)",
    "rp.strengths": "Points forts",
    "rp.improve": "À améliorer",
    "jm.title": "Compatibilité avec le poste",
    "jm.subtitle": "Comment votre CV + vos réponses se comparent aux compétences requises dans la description du poste.",
    "jm.matchScore": "Score de compatibilité",
    "jm.matched": "compatible",
    "jm.partial": "partiel",
    "jm.gap": "écart",
    "jm.gaps": "écarts",
    "jm.covered": (m, t) => `Compétences requises · ${m}/${t} couvertes`,
    "jm.statusMatch": "Compatible",
    "jm.statusPartial": "Partiel",
    "jm.statusGap": "Écart",
    "sp.hGaps": (n) => (n === 1 ? "votre principale lacune" : `vos ${n} principales lacunes`),
    "sen.eyebrow": "Niveau de l’entrevue (détecté)",
    "sen.entry":      "Niveau débutant",
    "sen.mid":        "Niveau intermédiaire",
    "sen.senior":     "Sénior IC",
    "sen.managerial": "Direction",
    "sen.executive":  "Cadre supérieur",
    "sen.hintDocs": "Nous avons calibré l’entrevue à ce niveau en fonction de vos documents. Aucun ajustement manuel — c’est ce qui rend la simulation réaliste.",
  },
};

// Legacy aliases: the OLD `session.language` field used the codes
// "pt-br" (Portuguese) and "both" (bilingual PT-BR + EN); both are mapped
// to the pt-BR bundle so historical sessions still render.
STRINGS["pt-br"] = STRINGS["pt-BR"];
STRINGS["both"]  = STRINGS["pt-BR"];


/**
 * Normalise whatever the caller passes into a bundle key.
 * - `null | undefined | ""` → `"en"`
 * - Exact BCP-47 code (`"pt-BR"`, `"es-419"`, `"fr-CA"`, `"en"`) → itself
 * - Legacy `"pt-br"` / `"both"` → `"pt-BR"`
 * - Anything Portuguese-flavored (`"pt"`, `"pt-PT"`, `"pt-br"`) → `"pt-BR"`
 * - Anything Spanish-flavored (`"es"`, `"es-MX"`, `"es-419"`) → `"es-419"`
 * - Anything French-flavored (`"fr"`, `"fr-CA"`, `"fr-FR"`) → `"fr-CA"`
 * - Anything else → `"en"`
 */
export function normalizeLang(input) {
  const raw = (input || "").toString().trim();
  if (!raw) return "en";
  if (STRINGS[raw]) return raw === "pt-br" || raw === "both" ? "pt-BR" : raw;
  const low = raw.toLowerCase();
  if (low.startsWith("pt") || low === "both") return "pt-BR";
  if (low.startsWith("es")) return "es-419";
  if (low.startsWith("fr")) return "fr-CA";
  return "en";
}


export function makeT(lang) {
  const code = normalizeLang(lang);
  const table = STRINGS[code] || STRINGS.en;
  return (key, ...args) => {
    const v = table[key];
    if (typeof v === "function") return v(...args);
    if (v !== undefined) return v;
    // Fallback to English if the localized bundle doesn't have this key yet.
    const fallback = STRINGS.en[key];
    if (typeof fallback === "function") return fallback(...args);
    return fallback ?? key;
  };
}
